import type { Event } from '@bugsnag/js'

// Builds the Bugsnag groupingHash + dashboard title for every error.
//
// Without it, errors collapse into a few useless mega-issues: every brand,
// distribution and failed target piled under one static category row. We rebuild
// both as `<brand> · <category>: <what failed>`, so one problem on one brand is one row.
//
// The details are stored on the error object under PIPE_ERR_KEY (by the inline
// <head> script and by notifyBugsnag); the config `onError` reads them off
// `event.originalError`. They live on the error, not on event metadata, because
// global onError callbacks run before the per-notify callback - so only a property
// on the error is reliably visible to both.

// Key the details are stored under. The inline <head> script hardcodes this exact
// string (it can't import), so keep both equal. Bracket access avoids the
// leading-underscore naming lint.
export const PIPE_ERR_KEY = '__pipeErrInfo'

// Error details captured at the throw site, read back at grouping time.
export interface PipeErrInfo {
  // Category, e.g. '[Pre-hydration] Fetch' or '[PipeOrders] Queue'.
  category: string
  // Resource failures carry tag + src; fetch failures carry url + method.
  tag?: unknown
  src?: unknown
  url?: unknown
  method?: unknown
  // useFetch's FetchError.kind ('timeout' | 'network' | 'http'), lifted by notifyBugsnag.
  failureKind?: unknown
  [key: string]: unknown
}

// groupingHash and errorClass are the same string, so the row title always
// describes exactly what was grouped. `url` / `method` are the normalized target
// resolved per category (full URL, HTTP method) - the caller writes them to the
// `metadata` tab as standard props so a dashboard pivot on url/method matches every
// error type, not just Fetch. Absent for errors with no network target.
export interface GroupingResult {
  groupingHash: string
  errorClass: string
  url?: string
  method?: string
}

// Read the details back off an error. Null when absent (Vue handler,
// bugsnag_notify, external notifies) - those keep Bugsnag's default grouping.
export function readPipeErrInfo(error: unknown): PipeErrInfo | null {
  if (!error || typeof error !== 'object') return null
  const info = (error as Record<string, unknown>)[PIPE_ERR_KEY]
  if (!info || typeof info !== 'object') return null
  if (typeof (info as Record<string, unknown>).category !== 'string') return null
  return info as PipeErrInfo
}

// Attach the details to the error (no-op if already set, or the error is frozen).
export function attachPipeErrInfo({ error, info }: { error: unknown; info: PipeErrInfo }): void {
  if (!error || typeof error !== 'object') return
  const target = error as Record<string, unknown>
  if (target[PIPE_ERR_KEY]) return
  try {
    target[PIPE_ERR_KEY] = info
  } catch {
    // frozen/exotic error - skip
  }
}

// Host for grouping. Relative URLs resolve against the serving origin (so a
// same-origin chunk groups under the brand domain); bad inputs return a stable
// token instead of throwing.
export function extractHost({ url, base }: { url: unknown; base?: string }): string {
  if (typeof url !== 'string' || url === '') return 'unknown'
  const lower = url.toLowerCase()
  if (lower.startsWith('data:')) return 'data'
  if (lower.startsWith('blob:')) return 'blob'
  const origin = base ?? (typeof location !== 'undefined' ? location.origin : undefined)
  try {
    return new URL(url, origin).hostname || 'unknown'
  } catch {
    return 'unparseable'
  }
}

// Collapse volatile parts (uuids, hex tokens, multi-digit ids) so one logical error
// doesn't split into many groups keyed by order id or build hash.
export function normalizeMessage(msg: unknown): string {
  const text = typeof msg === 'string' ? msg : String(msg ?? '')
  return text
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':uuid')
    .replace(/\b[0-9a-f]{16,}\b/gi, ':token')
    .replace(/\b\d{2,}\b/g, ':id')
    .trim()
    .slice(0, 200)
}

// Browser phrasings for a dead network request: Chrome "Failed to fetch", Safari
// "Load failed", Firefox "NetworkError".
export function looksLikeFetchFailure(msg: unknown): boolean {
  const text = typeof msg === 'string' ? msg : ''
  return /failed to fetch|load failed|network\s?error|err_/i.test(text)
}

// Recover the failed URL from Bugsnag's network breadcrumbs when the error lacks
// one (bare "Failed to fetch", or XHR-based SDKs our fetch patch misses). Bugsnag
// leaves a 'request' crumb with { method, url, status, duration }.
export function lastRequestBreadcrumbUrl(event: Event): { url?: string; method?: string } {
  const crumbs = event.breadcrumbs ?? []
  for (let i = crumbs.length - 1; i >= 0; i--) {
    const crumb = crumbs[i]
    if (crumb?.type === 'request') {
      const url = crumb.metadata?.url
      const method = crumb.metadata?.method
      return {
        url: typeof url === 'string' ? url : undefined,
        method: typeof method === 'string' ? method : undefined,
      }
    }
  }
  return {}
}

// Build the groupingHash + matching title. `brandLabel` (brandCode, else the
// serving domain) leads both, so one brand's errors cluster regardless of which
// domain served them (the full domain lives in the `pipe` metadata tab).
//
// Shape: `<brand> · <category>: <specifics>`. The category is used verbatim (keeps
// '[Pre-hydration]' vs '[Runtime]' distinct); what follows the colon depends on it
// - target host for resource/fetch, normalized message otherwise. Matched by
// substring (survives label tweaks); check 'Unhandled Rejection' before the
// catch-all that also covers '[*] Error' and app contexts.
export function computeGrouping({
  info,
  event,
  brandLabel,
}: {
  info: PipeErrInfo
  event: Event
  brandLabel: string
}): GroupingResult {
  const category = info.category
  const message = event.errors[0]?.errorMessage ?? ''
  // groupingHash === errorClass: the row title is the grouping key. `url` / `method`
  // ride along to the caller, which writes them to the `metadata` tab.
  const toResult = ({
    specifics,
    url,
    method,
  }: {
    specifics: string
    url?: string
    method?: string
  }): GroupingResult => {
    const title = `${brandLabel} · ${category}${specifics ? ': ' + specifics : ''}`
    return { groupingHash: title, errorClass: title, url, method }
  }

  if (category.includes('Resource')) {
    const tag = typeof info.tag === 'string' ? info.tag : 'RESOURCE'
    const src = typeof info.src === 'string' ? info.src : undefined
    // Resource loads have no HTTP method
    return toResult({
      specifics: `${tag} from ${extractHost({ url: info.src })}`,
      url: src,
    })
  }

  if (category.includes('Fetch')) {
    const url = typeof info.url === 'string' ? info.url : undefined
    const method = typeof info.method === 'string' ? info.method : 'GET'
    return toResult({
      specifics: `${method} to ${extractHost({ url: info.url })}`,
      url,
      method,
    })
  }

  if (category.includes('Unhandled Rejection')) {
    // Most fetch failures carry a url, but for bare TypeErrors that don't,
    // so recover it from breadcrumbs.
    if (!info.url && looksLikeFetchFailure(message)) {
      const recovered = lastRequestBreadcrumbUrl(event)
      if (recovered.url) {
        const method = recovered.method ?? 'GET'
        return toResult({
          specifics: `${method} to ${extractHost({ url: recovered.url })}`,
          url: recovered.url,
          method,
        })
      }
    }
    return toResult({ specifics: normalizeMessage(message) })
  }

  // A useFetch request that got no response is one failure worded per browser
  // (Chrome "Failed to fetch", Safari "Load failed", Firefox "NetworkError …") -
  // group on the kind so one outage is one row. The wording stays the message.
  if (info.failureKind === 'network') return toResult({ specifics: 'Network error' })

  // '[*] Error' + every app context: full category + normalized message.
  return toResult({ specifics: normalizeMessage(message) })
}
